/**
 * MoneyMark.jsx: the $ mark on a to-do. A faint grey $ when unmarked, gold when
 * marked; a click toggles it. Only a mark: nothing sorts, filters or counts by it.
 * Anyone who can see a to-do may toggle it (the server checks).
 *
 * Used on To Do rows, the Home card and (with `label`) in the to-do dialog.
 * Its click never reaches the row (that would open the dialog or tick it), and it
 * is not a drag handle. 44px tap target on phones, compact on desktop (`dense`:
 * the Home card's smaller rows).
 *
 * It sits on rows that show to-do text, so it takes a static data-track name.
 */
import { Button, IconButton } from '@mui/material';
import { AttachMoney as MoneyIcon } from '@mui/icons-material';

// 3.6:1 on white and 3.3:1 on a hovered row (non-text contrast needs 3:1);
// the theme's #B8860B drops to 3.0:1 on a hovered row.
export const MONEY_GOLD = '#AD7F0A';

export default function MoneyMark({ marked, onToggle, trackKey, label, dense = false, sx }) {
  const props = {
    'data-track': trackKey,
    'aria-label': marked ? 'Clear money mark' : 'Mark as money',
    'aria-pressed': !!marked,
    onClick: (e) => {
      e.stopPropagation();
      onToggle();
    },
  };
  const color = marked ? MONEY_GOLD : 'text.disabled';

  if (label) {
    return (
      <Button
        {...props}
        startIcon={<MoneyIcon />}
        sx={{
          alignSelf: 'flex-start',
          minHeight: 44,
          textTransform: 'none',
          // Gold is for the icon only: as text it is under 4.5:1.
          color: marked ? 'text.primary' : 'text.secondary',
          fontWeight: marked ? 600 : 400,
          '& .MuiButton-startIcon': { mr: 0.5, color },
          '&:hover .MuiButton-startIcon': { color: marked ? MONEY_GOLD : 'text.secondary' },
          ...sx,
        }}
      >
        {label}
      </Button>
    );
  }

  return (
    <IconButton
      {...props}
      sx={{
        flexShrink: 0,
        width: { xs: 44, sm: dense ? 28 : 30 },
        height: { xs: 44, sm: dense ? 28 : 36 },
        p: 0,
        borderRadius: 1,
        color,
        '&:hover': { color: marked ? MONEY_GOLD : 'text.secondary' },
        ...sx,
      }}
    >
      <MoneyIcon sx={{ fontSize: dense ? 18 : 20 }} />
    </IconButton>
  );
}
